import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useEffect, isValidElement, cloneElement, Children, useState } from 'react';
import useRefs from '../../hooks/useRefs';
import useScrollParent from '../../hooks/useScrollParent';
import Sticky from '../sticky';
import TabsTitle from './tabsTitle';
import TabPane from './tabPane';
import { scrollLeftTo, getElementTop, getVisibleHeight, scrollTopTo, getVisibleTop } from '../../utils/dom';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import useScroll from '../../hooks/useScroll';
import classnames from 'classnames';
import useConfig from '../../hooks/useConfig';
var cls = 'tabs';
/**
 * todo
 *  支持锚点嵌套
 */

var Tabs = function Tabs(props) {
  var children = props.children,
      color = props.color,
      background = props.background,
      type = props.type,
      duration = props.duration,
      className = props.className,
      titleActiveColor = props.titleActiveColor,
      onChange = props.onChange,
      titleInactiveColor = props.titleInactiveColor,
      scrollable = props.scrollable,
      offsetTop = props.offsetTop;

  var _useConfig = useConfig(),
      createNamespace = _useConfig.createNamespace,
      prefixCls = _useConfig.prefixCls;

  var classes = classnames(createNamespace === null || createNamespace === void 0 ? void 0 : createNamespace(cls, prefixCls), className);
  var root = useRef(null);
  var wrapRef = useRef(null);
  var immediateRef = useRef(true);
  var navRef = useRef(null);
  var scroller = useScrollParent(root);

  var _useRefs = useRefs(),
      titleRefs = _useRefs[0],
      setTitleRefs = _useRefs[1];

  var _useRefs2 = useRefs(),
      contentRefs = _useRefs2[0],
      setContentRefs = _useRefs2[1];

  var lockScroll = useRef(false);
  var wrapHeight = useMemo(function () {
    if (!wrapRef.current) return 0;
    return getVisibleHeight(wrapRef.current); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapRef.current]);
  var childrenList = useMemo(function () {
    var tabPaneList = [];
    Children.forEach(children, function (child) {
      if ( /*#__PURE__*/isValidElement(child) && child.type === TabPane) {
        tabPaneList.push(child);
      }
    });
    return tabPaneList;
  }, [children]);

  var _useState = useState(0),
      index = _useState[0],
      setIndex = _useState[1];

  var navStyle = useMemo(function () {
    return {
      background: background
    };
  }, [background]);
  var lineStyle = useMemo(function () {
    var title = titleRefs === null || titleRefs === void 0 ? void 0 : titleRefs[index];
    var measureStyle = {
      backgroundColor: color,
      transitionDuration: (immediateRef.current ? 0 : duration) + "ms",
      width: title === null || title === void 0 ? void 0 : title.offsetWidth,
      transform: "translateX(" + (title === null || title === void 0 ? void 0 : title.offsetLeft) + "px) translateY(-50%)"
    };
    return measureStyle; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color, immediateRef.current, duration, index]);

  var setCurrentIndex = function setCurrentIndex(currentIndex) {
    setIndex(currentIndex);
    onChange === null || onChange === void 0 ? void 0 : onChange(currentIndex);
  };

  var scrollToCurrentContent = function scrollToCurrentContent(current) {
    var contentTarget = contentRefs[current];

    if (contentTarget && scroller) {
      var to = Math.ceil(getElementTop(contentTarget, scroller) - offsetTop - wrapHeight);
      lockScroll.current = true;
      scrollTopTo(scroller, to, 0, function () {
        lockScroll.current = false;
      });
    }
  };

  var onClickTab = function onClickTab(targetIndex) {
    if (targetIndex !== index) {
      setCurrentIndex(targetIndex);
      scrollToCurrentContent(targetIndex);
    }
  };

  var scrollIntoView = function scrollIntoView(immediate) {
    var nav = navRef.current;
    var title = titleRefs === null || titleRefs === void 0 ? void 0 : titleRefs[index];

    if (!scrollable || !nav || !title) {
      return;
    }

    var to = title.offsetLeft - (nav.offsetWidth - title.offsetWidth) / 2;
    scrollLeftTo(nav, to, immediate ? 0 : +duration);
  };

  var getCurrentIndexOnScroll = function getCurrentIndexOnScroll() {
    var scrollOffset = wrapHeight + offsetTop;

    for (var _index = 0; _index < contentRefs.length; _index++) {
      var $el = contentRefs[_index];
      var top = getVisibleTop($el);

      if (top > scrollOffset) {
        return _index === 0 ? 0 : _index - 1;
      }
    }

    return titleRefs.length - 1;
  };

  var onScroll = function onScroll() {
    if (lockScroll.current) return;
    var currentIndex = getCurrentIndexOnScroll();

    if (currentIndex !== index) {
      setCurrentIndex(currentIndex);
    }
  };

  var renderNav = function renderNav() {
    return childrenList.map(function (item, itemIndex) {
      return _jsx(TabsTitle, {
        ref: setTitleRefs(itemIndex),
        type: type,
        title: item.props.title,
        color: "transparent",
        className: type,
        isActive: itemIndex === index,
        scrollable: scrollable,
        activeColor: titleActiveColor,
        inactiveColor: titleInactiveColor,
        onClick: function onClick() {
          onClickTab(itemIndex);
        }
      }, item.key !== undefined ? String(item.key) : undefined);
    });
  };

  var renderHeader = function renderHeader() {
    return _jsx("div", Object.assign({
      ref: wrapRef,
      className: createNamespace === null || createNamespace === void 0 ? void 0 : createNamespace(cls + "-title-group", prefixCls)
    }, {
      children: _jsxs("div", Object.assign({
        ref: navRef,
        role: "tablist",
        style: navStyle,
        className: classnames(createNamespace === null || createNamespace === void 0 ? void 0 : createNamespace(cls + "-title-nav", prefixCls), type, {
          scrollable: scrollable
        })
      }, {
        children: [renderNav(), type === 'text' && !immediateRef.current && _jsx("div", {
          style: lineStyle,
          className: createNamespace === null || createNamespace === void 0 ? void 0 : createNamespace(cls + "-title-slider", prefixCls)
        })]
      }))
    }));
  };

  useUpdateEffect(function () {
    scrollIntoView();
  }, [index]);
  useEffect(function () {
    immediateRef.current = false;
    scrollIntoView(true); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useScroll(scroller, onScroll);
  return _jsxs("div", Object.assign({
    ref: root,
    className: classes
  }, {
    children: [_jsx(Sticky, Object.assign({
      offset: offsetTop,
      container: root
    }, {
      children: renderHeader()
    })), Children.toArray(children).filter(Boolean).map(function (node, index) {
      return /*#__PURE__*/isValidElement(node) ? /*#__PURE__*/cloneElement(node, {
        index: index,
        ref: setContentRefs(index)
      }) : node;
    })]
  }));
};

Tabs.defaultProps = {
  type: 'text',
  duration: 300,
  offsetTop: 0
};
Tabs.displayName = 'Tabs';
export default Tabs;